// ADR-132: pure helpers for the Job Position Mappings page.
// Pattern rules mirror radar-ingestion's valueTransformationMatcher (ADR-015):
// whitespace is collapsed, `*` is the only wildcard, a bare `*` is the
// catch-all, everything else is literal and case-insensitive.

import {
  INGESTION_MAPPING_OUTCOMES,
  INGESTION_PATTERN_MATCH_TYPES,
  INGESTION_SOURCE_PATTERN_MAX_LENGTH,
} from '../constants/ingestion';

export const normalizeSourcePattern = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().replace(/\s+/g, ' ');
};

export const classifySourcePattern = (value) => {
  const pattern = normalizeSourcePattern(value);
  if (!pattern) {
    return null;
  }
  if (pattern === '*') {
    return INGESTION_PATTERN_MATCH_TYPES.CATCH_ALL;
  }
  if (pattern.includes('*')) {
    return INGESTION_PATTERN_MATCH_TYPES.PATTERN;
  }
  return INGESTION_PATTERN_MATCH_TYPES.EXACT;
};

/** What a pattern means, in words, for the dialog's live hint. */
export const describeSourcePattern = (value) => {
  const pattern = normalizeSourcePattern(value);
  const type = classifySourcePattern(pattern);
  if (!type) {
    return '';
  }
  if (type === INGESTION_PATTERN_MATCH_TYPES.CATCH_ALL) {
    return 'Matches every title that has no more specific mapping. Nothing falls through to automatic (fuzzy) position matching.';
  }
  if (type === INGESTION_PATTERN_MATCH_TYPES.EXACT) {
    return `Matches the title "${pattern}" exactly (case-insensitive).`;
  }
  const literal = pattern.replace(/\*/g, '');
  const starts = pattern.startsWith('*');
  const ends = pattern.endsWith('*');
  const middle = pattern.slice(starts ? 1 : 0, ends ? -1 : undefined);
  if (!middle.includes('*')) {
    if (starts && ends) {
      return `Matches any title containing "${literal}".`;
    }
    if (ends) {
      return `Matches any title starting with "${literal}".`;
    }
    if (starts) {
      return `Matches any title ending with "${literal}".`;
    }
  }
  return 'Matches titles where each * stands for any text (case-insensitive).';
};

/**
 * Client-side validation, mirroring the server. Returns { errors[], warnings[] }.
 * The server remains the authority; this only saves a round trip.
 */
export const validateMappingDraft = ({ sourcePattern, outcome, positionId }) => {
  const errors = [];
  const warnings = [];
  const pattern = normalizeSourcePattern(sourcePattern);

  if (!pattern) {
    errors.push('Enter a source title or pattern.');
  } else if (pattern.length > INGESTION_SOURCE_PATTERN_MAX_LENGTH) {
    errors.push(`The pattern can be at most ${INGESTION_SOURCE_PATTERN_MAX_LENGTH} characters.`);
  }

  if (outcome === INGESTION_MAPPING_OUTCOMES.MAP_TO_POSITION && !positionId) {
    errors.push('Choose the Radar position this title maps to.');
  }

  const type = classifySourcePattern(pattern);
  if (type === INGESTION_PATTERN_MATCH_TYPES.CATCH_ALL) {
    warnings.push('A bare * catches every title of this provider that has no more specific mapping.');
  } else if (type === INGESTION_PATTERN_MATCH_TYPES.PATTERN && pattern.replace(/\*/g, '').trim().length < 3) {
    warnings.push('This pattern has very little literal text and may match many unrelated titles.');
  }

  return { errors, warnings };
};

/** Search / filter the table client-side (the whole set is loaded at once). */
// A filter is a list of selected values (pill toggles); empty or 'ALL' = no filter.
const toSelection = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  return value && value !== 'ALL' ? [value] : [];
};

export const filterMappings = (mappings = [], { search = '', outcome = [], matchType = [] } = {}) => {
  const needle = search.trim().toLowerCase();
  const outcomes = toSelection(outcome);
  const matchTypes = toSelection(matchType);
  return mappings.filter((mapping) => {
    if (outcomes.length > 0 && !outcomes.includes(mapping.outcome)) {
      return false;
    }
    if (matchTypes.length > 0 && !matchTypes.includes(mapping.matchType || classifySourcePattern(mapping.sourcePattern))) {
      return false;
    }
    if (!needle) {
      return true;
    }
    return [mapping.sourcePattern, mapping.position?.label, mapping.notes]
      .filter(Boolean)
      .some((text) => String(text).toLowerCase().includes(needle));
  });
};

const describeTarget = (snapshot) => {
  if (!snapshot) {
    return '';
  }
  return snapshot.outcome === INGESTION_MAPPING_OUTCOMES.NO_POSITION
    ? 'no position'
    : (snapshot.positionLabel || 'a position');
};

/** One readable line for a history entry. */
export const describeHistoryEntry = (entry) => {
  const before = entry?.before || null;
  const after = entry?.after || null;
  const pattern = after?.sourcePattern || before?.sourcePattern || '';

  if (before && after) {
    const changes = [];
    if (before.sourcePattern !== after.sourcePattern) {
      changes.push(`pattern "${before.sourcePattern}" → "${after.sourcePattern}"`);
    }
    if (describeTarget(before) !== describeTarget(after)) {
      changes.push(`${describeTarget(before)} → ${describeTarget(after)}`);
    }
    if ((before.notes || '') !== (after.notes || '')) {
      changes.push('notes');
    }
    return `"${pattern}": ${changes.length > 0 ? changes.join('; ') : 'no visible change'}`;
  }
  if (after) {
    return `"${pattern}" → ${describeTarget(after)}`;
  }
  return `"${pattern}" (was ${describeTarget(before)})`;
};

/**
 * History table row: { title, change } — the job title the entry is about and
 * what happened to its result ("→ Director", "Manager → Director", "was no position").
 */
export const describeHistoryRow = (entry) => {
  const before = entry?.before || null;
  const after = entry?.after || null;
  const title = after?.sourcePattern || before?.sourcePattern || '';
  // Same wording as the Result column / outcome label ("No position").
  const target = (snapshot) => {
    const text = describeTarget(snapshot);
    return text === 'no position' ? 'No position' : text;
  };

  if (before && after) {
    const changes = [];
    if (before.sourcePattern !== after.sourcePattern) {
      changes.push(`renamed from "${before.sourcePattern}"`);
    }
    if (describeTarget(before) !== describeTarget(after)) {
      changes.push(`${target(before)} → ${target(after)}`);
    }
    const notesBefore = (before.notes || '').trim();
    const notesAfter = (after.notes || '').trim();
    if (notesBefore !== notesAfter) {
      if (!notesBefore) {
        changes.push('notes added');
      } else if (!notesAfter) {
        changes.push('notes removed');
      } else {
        changes.push('notes changed');
      }
    }
    return { title, change: changes.length > 0 ? changes.join('; ') : 'no visible change' };
  }
  if (after) {
    return { title, change: `mapped to ${target(after)}` };
  }
  return { title, change: `previously mapped to ${target(before)}` };
};

/**
 * History list: consecutive entries of one set version (one save or one
 * import) become one group, so who/when is shown once per change set.
 */
export const groupHistoryByVersion = (entries = []) => {
  const groups = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.setVersion === entry.setVersion) {
      last.entries.push(entry);
    } else {
      groups.push({
        setVersion: entry.setVersion,
        changedBy: entry.changedBy,
        changedAt: entry.changedAt,
        isImport: String(entry.action || '').startsWith('IMPORT_'),
        entries: [entry],
      });
    }
  }
  return groups;
};

/**
 * Parse an import file. Accepts the export shape ({ provider, items }), the
 * radar-ingestion seed shape (same), a bare items array, or a legacy plain map
 * ({ "Title": "Position" | "" }) copied from the old code config.
 * Returns { provider|null, items[] } or throws with a readable message.
 */
export const parseImportPayload = (text) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('The file is not valid JSON.');
  }

  if (Array.isArray(parsed)) {
    return { provider: null, items: parsed };
  }
  if (parsed && Array.isArray(parsed.items)) {
    return { provider: parsed.provider || null, items: parsed.items };
  }
  if (parsed && typeof parsed === 'object') {
    const entries = Object.entries(parsed);
    if (entries.length > 0 && entries.every(([, value]) => typeof value === 'string')) {
      return {
        provider: null,
        items: entries.map(([sourcePattern, positionLabel]) => ({ sourcePattern, positionLabel })),
      };
    }
  }
  throw new Error('Expected { "items": [...] }, an array of items, or a { "title": "position" } map.');
};

export const buildExportFileName = (provider, version) => {
  const stamp = new Date().toISOString().slice(0, 10);
  return `job-position-mappings-${String(provider).toLowerCase()}-v${version ?? 0}-${stamp}.json`;
};
